import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Box, Text, VStack, Icon, useToast, Spinner, Flex } from '@chakra-ui/react';
import { FiUpload, FiFile, FiCheckCircle, FiXCircle } from 'react-icons/fi';
import AccountApi from 'api/account';

const FileUploader = ({ onClose, onUploadSuccess, productId }) => {
  const toast = useToast();
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadedCount, setUploadedCount] = useState(0);
  const [fileStatus, setFileStatus] = useState({});

  const uploadFile = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('product_id', productId);

    try {
      await AccountApi.Store(formData);
      setFileStatus((prevStatus) => ({ ...prevStatus, [file.name]: 'success' }));
      toast({
        title: 'فایل با موفقیت آپلود شد',
        description: `فایل ${file.name} با موفقیت آپلود شد.`,
        status: 'success',
        duration: 2500,
        position: 'top-right',
      });
      setUploadedCount((prevCount) => prevCount + 1);
    } catch (error) {
      setFileStatus((prevStatus) => ({ ...prevStatus, [file.name]: 'error' }));
      if (error.response && error.response.data.errors) {
        const errorMessages = Object.values(error.response.data.errors).flat().join('\n');
        toast({
          title: 'خطا',
          description: errorMessages,
          status: 'error',
          duration: 2500,
          position: 'top-right',
        });
      } else {
        toast({
          title: 'خطا در آپلود فایل',
          description: `مشکلی در هنگام آپلود فایل ${file.name} به وجود آمد.`,
          status: 'error',
          duration: 2500,
          position: 'top-right',
        });
      }
    }
  };

  const onDrop = useCallback(async (acceptedFiles) => {
    setFiles(acceptedFiles);
    setUploading(true);

    for (const file of acceptedFiles) {
      setFileStatus((prevStatus) => ({ ...prevStatus, [file.name]: 'uploading' }));
      await uploadFile(file);
    }

    // All files have been uploaded
    setUploading(false);
    onUploadSuccess();
  }, [onUploadSuccess, onClose, productId]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop, accept: '.xlsx, .xls', multiple: true });

  return (
    <Box
      {...getRootProps()}
      border="2px dashed #ccc"
      p={4}
      textAlign="center"
      cursor="pointer"
      bg={isDragActive ? 'gray.100' : 'white'}
      borderColor={isDragActive ? 'blue.400' : '#ccc'}
      transition="background-color 0.2s, border-color 0.2s"
    >
      <input {...getInputProps()} />
      <VStack spacing={4}>
        <Icon as={FiUpload} boxSize={12} />
        <Text>فایل ها را اینجا رها کنید...</Text>
      </VStack>
      <Box mt={4}>
        {files.map((file) => (
          <Flex key={file.name} align="center" mt={2}>
            <Icon as={FiFile} boxSize={6} mr={2} />
            <Text fontSize="sm" flex="1">{file.name}</Text>
            {fileStatus[file.name] === 'uploading' && <Spinner size="sm" />}
            {fileStatus[file.name] === 'success' && <Icon as={FiCheckCircle} boxSize={6} color="green.500" />}
            {fileStatus[file.name] === 'error' && <Icon as={FiXCircle} boxSize={6} color="red.500" />}
          </Flex>
        ))}
      </Box>
    </Box>
  );
};

export default FileUploader;
